import { useCallback, useState } from "react";

import {
  useDataProvider,
  useGetIdentity,
  useNotification,
} from "@refinedev/core";

import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import {
  type EnrichmentEntity,
  isEmbeddableEntity,
} from "@/lib/tina/content-types";
import type { EnrichmentMode } from "@/lib/tina/enrichment-jobs";
import type { IIdentity } from "@/providers/auth-provider";

/**
 * `POST /enrichments` — queue an enrichment/embedding job from IMS.
 *
 * The intake accepts the caller `ims` (added explicitly "so a curator re-enrich
 * from IMS needs no auth change") and batches of up to 500, so the whole
 * feature is this one hook on the **tina** provider.
 *
 * **Surfacing the dedup semantics is the point of the feature.** The
 * intake answers `202` for every accepted body — including one whose every job
 * was already queued — because its primary caller is a Cloud Task that retries
 * any non-2xx. So "nothing happened" is a *success* response with an empty
 * `queued`, and a UI that assumes a 2xx means "queued" lies to the curator. The
 * toast is therefore built from the response, never from the request:
 *
 * | response | toast |
 * |---|---|
 * | all queued | success — "Queued N enrichment jobs" |
 * | mixed | success — "Queued N · M already queued", skipped ids in the description |
 * | all skipped (`run_id === null`) | **not an error** — "Already queued: nothing to do." |
 *
 * That last row is the honest rendering of the two-layer dedup (the backend's
 * 10-minute named-task bucket plus Tina's `ev_enrichment_active_uq` index), and
 * it is why the intake returns 202 rather than 409 in the first place.
 */

export interface EnrichmentJobRequest {
  entity_type: EnrichmentEntity;
  entity_id: string;
  mode?: EnrichmentMode;
}

export interface QueuedJob {
  entity_type: string;
  entity_id: string;
  mode: string;
  event_id: string;
}

export interface SkippedJob {
  entity_type: string;
  entity_id: string;
  reason: "already_queued";
}

export interface EnrichmentIntakeResponse {
  run_id: string | null;
  queued: QueuedJob[];
  already_queued: SkippedJob[];
}

/** Tina's `MAX_JOBS_PER_REQUEST`. A larger body is a 422, not a partial queue. */
export const MAX_ENRICHMENT_JOBS_PER_REQUEST = 500;

/**
 * The two `source` values the `ims` token is entitled to claim
 * (`CALLER_SOURCE_KINDS` in Tina's intake schema). Claiming any other — notably
 * `backend-create`, which is the one value the fresh-create short-circuit fires
 * on — is a 403, by design: `source` is the provenance every "why did this job
 * exist?" query keys off.
 */
const SOURCE_SINGLE = "ims";
const SOURCE_BATCH = "bulk";

export interface UseEnrichmentIntakeResult {
  enqueue(job: EnrichmentJobRequest): Promise<EnrichmentIntakeResponse | null>;
  enqueueBatch(
    jobs: EnrichmentJobRequest[],
  ): Promise<EnrichmentIntakeResponse | null>;
  isPending: boolean;
}

export function useEnrichmentIntake(): UseEnrichmentIntakeResult {
  const { custom } = useDataProvider()("tina");
  const { data: identity } = useGetIdentity<IIdentity>();
  const { open } = useNotification();
  const [isPending, setIsPending] = useState(false);

  /**
   * The curator's Firebase uid — the same value `resolveHitl` stamps as
   * `resolved_by`.
   *
   * Deliberately **omitted** rather than defaulted when the identity has not
   * loaded. `requested_by` is echoed verbatim into `runs.trigger_spec`, into
   * every job row's `metadata.enrichment.requested_by` and into the structured
   * logs, and a non-uid placeholder there is unattributable forever — the exact
   * shape of the `hitl_resolved_by` pollution that put phantom "Former curators"
   * on the IMS leaderboard. An absent value is honest; a fake one is not.
   */
  const requestedBy = identity?.id;

  const post = useCallback(
    async (
      payload: Record<string, unknown>,
      jobCount: number,
    ): Promise<EnrichmentIntakeResponse | null> => {
      setIsPending(true);
      Sentry.addBreadcrumb({
        category: "tina-enrichments.intake",
        message: "Queueing enrichment jobs",
        level: "info",
        data: { jobs: jobCount, source: payload.source },
      });
      try {
        const response = await custom!({
          url: "",
          method: "post",
          payload: {
            ...payload,
            ...(requestedBy ? { requested_by: requestedBy } : {}),
          },
          meta: { urlPath: "enrichments" },
        });
        const body = response.data as EnrichmentIntakeResponse;
        notifyIntakeOutcome(open, body);
        return body;
      } catch (error) {
        Sentry.captureException(error);
        notifyMutationError(open, "Queue enrichment", error);
        return null;
      } finally {
        setIsPending(false);
      }
    },
    [custom, open, requestedBy],
  );

  const enqueue = useCallback(
    (job: EnrichmentJobRequest) => {
      assertModeIsAvailable(job);
      // The flat single-job form — what the backend's own Cloud Task sends.
      return post(
        {
          entity_type: job.entity_type,
          entity_id: job.entity_id,
          ...(job.mode ? { mode: job.mode } : {}),
          source: SOURCE_SINGLE,
        },
        1,
      );
    },
    [post],
  );

  const enqueueBatch = useCallback(
    (jobs: EnrichmentJobRequest[]) => {
      jobs.forEach(assertModeIsAvailable);
      if (jobs.length > MAX_ENRICHMENT_JOBS_PER_REQUEST) {
        throw new Error(
          `Enrichment intake accepts at most ${MAX_ENRICHMENT_JOBS_PER_REQUEST} jobs per request (${jobs.length} given)`,
        );
      }
      // The batch form. `mode` is per-job here and a top-level `mode` alongside
      // `jobs` is a 422 — accepting and ignoring it is what Tina refuses to do.
      return post({ jobs, source: SOURCE_BATCH }, jobs.length);
    },
    [post],
  );

  return { enqueue, enqueueBatch, isPending };
}

/**
 * `mode=embed` on a non-embeddable entity has no stage to run — Tina 422s it.
 * Failing here names the entity instead of surfacing a validation error from a
 * request the curator did not knowingly compose.
 */
function assertModeIsAvailable(job: EnrichmentJobRequest): void {
  if (job.mode === "embed" && !isEmbeddableEntity(job.entity_type)) {
    throw new Error(
      `'${job.entity_type}' has no embedding, so mode 'embed' is not available for it`,
    );
  }
}

type NotifyFn = ReturnType<typeof useNotification>["open"];

function notifyIntakeOutcome(
  open: NotifyFn,
  body: EnrichmentIntakeResponse,
): void {
  const queued = body.queued?.length ?? 0;
  const skipped = body.already_queued?.length ?? 0;

  if (queued === 0) {
    // Not an error, and not a failure to report as one: an active job already
    // exists for every entity asked for. Refine's notification contract has no
    // `info` level, so this rides on `success` — the wording carries the
    // meaning, and a red toast for a correctly-debounced request would teach
    // curators to re-press the button.
    open?.({
      type: "success",
      message: "Already queued: nothing to do",
      description:
        "At most one active job per entity (Tina's queue-uniqueness index). It will run on the next drain.",
    });
    return;
  }

  open?.({
    type: "success",
    message:
      skipped > 0
        ? `Queued ${queued} · ${skipped} already queued`
        : `Queued ${queued} enrichment job${queued === 1 ? "" : "s"}`,
    description: describeIntake(body, skipped),
  });
}

function describeIntake(
  body: EnrichmentIntakeResponse,
  skipped: number,
): string | undefined {
  const parts: string[] = [];
  if (body.run_id) parts.push(`Run ${body.run_id.slice(0, 8)}`);
  if (skipped > 0) {
    const ids = body.already_queued.map((job) => job.entity_id);
    const shown = ids.slice(0, 5).join(", ");
    parts.push(
      `Skipped: ${shown}${ids.length > 5 ? ` (+${ids.length - 5} more)` : ""}`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
}
